import { Typography } from '@mui/material';
import { SimpleShowLayout, FunctionField, Edit, SimpleForm, maxLength, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import EditableField from '@/components/global/fields/EditableField';
import { CustomRichText } from '@/components/global/inputs/CustomRichText';
import { Palette } from '@/themes/themeOverrides/Palette';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const ServiceNoteSection = ({ elementId, collectionPoint, handleSave, canEditServiceNote, canViewServiceNote, editingStates }) => {
    const translate = useTranslate();
    const { isEditing, FIELDS, startEditingServiceNote } = editingStates;

    if (!canViewServiceNote) {
        return null;
    }

    if (canEditServiceNote) {
        return (
            <Edit
                id={elementId}
                resource='collection-point'
                actions={false}
                sx={{
                    '& .MuiCardContent-root': {
                        marginBottom: '0 !important',
                    },
                    '& form': {
                        paddingBottom: '0 !important',
                    },
                }}
            >
                <SimpleForm toolbar={false} onSubmit={handleSave} record={collectionPoint}>
                    <SimpleShowLayout
                        sx={{
                            width: '100%',
                            '& .MuiBox-root': {
                                width: '100%',
                                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                    marginTop: '10px',
                                },
                                '& .tiptap.ProseMirror': {
                                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                        paddingBottom: '160px',
                                    },
                                },
                            },
                            '& .MuiStack-root.ra-field': {
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                borderTop: `2px solid ${Palette.gray.gray75}`,
                                marginBottom: '0',
                                padding: '16px 0',
                                width: '100%',
                                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                    padding: '20px 0',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    marginTop: '10px',
                                },
                                '&:has(.Mui-error)': {
                                    '& .MuiToggleButtonGroup-root:nth-of-type(1)': {
                                        bottom: 104,
                                    },
                                    '& .MuiToggleButtonGroup-root:nth-of-type(2)': {
                                        bottom: 62,
                                    },
                                },
                                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                    '& .MuiToggleButtonGroup-root': {
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                    },
                                    '& .MuiToggleButton-root': {
                                        margin: '0',
                                    },
                                },
                            },
                            '& .MuiFormHelperText-root.ra-rich-text-input-error': {
                                display: 'block',
                                marginTop: '10px !important',
                            },
                        }}
                    >
                        <EditableField
                            label={translate('resources.collectionPoint.fields.serviceNote')}
                            isDescritpionField={true}
                            isEditing={isEditing(FIELDS.SERVICE_NOTE)}
                            onEdit={startEditingServiceNote}
                            displayValue={record => {
                                if (!record?.serviceNote) {
                                    return translate('common.shortage');
                                }
                                return record.serviceNote;
                            }}
                            isFilled={record => !!record?.serviceNote}
                            isHtml={true}
                            editComponent={
                                <CustomRichText
                                    source='serviceNote'
                                    fullWidth
                                    validate={maxLength(1000, translate('common.validation.maxLength', { count: 1000 }))}
                                />
                            }
                            canEdit={true}
                            className={isEditing(FIELDS.SERVICE_NOTE) ? 'editable-field--active' : 'editable-field'}
                        />
                    </SimpleShowLayout>
                </SimpleForm>
            </Edit>
        );
    }

    return (
        <SimpleShowLayout>
            <FunctionField
                label={translate('resources.collectionPoint.fields.serviceNote')}
                render={record => {
                    if (!record.serviceNote) {
                        return (
                            <Typography variant='caption' style={{ color: Palette.gray.gray400 }}>
                                {translate('common.shortage')}
                            </Typography>
                        );
                    }
                    return <Typography variant='caption' className='no-bold' dangerouslySetInnerHTML={{ __html: record.serviceNote }} />;
                }}
            />
        </SimpleShowLayout>
    );
};

ServiceNoteSection.propTypes = {
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    collectionPoint: PropTypes.object.isRequired,
    handleSave: PropTypes.func.isRequired,
    canEditServiceNote: PropTypes.bool.isRequired,
    canViewServiceNote: PropTypes.bool.isRequired,
    editingStates: PropTypes.shape({
        isEditing: PropTypes.func.isRequired,
        FIELDS: PropTypes.object.isRequired,
        startEditingServiceNote: PropTypes.func.isRequired,
    }).isRequired,
};

export default ServiceNoteSection;
